/**
 * `useCampaignTemplates` — TH-55110 (clone-and-customize) + TH-55143 (M-campT.5).
 *
 * Wrapper TanStack Query do catálogo de templates de campanha.
 *
 * - GET `/api/campaign-templates` lista o catálogo global (TH-55140).
 *   Aceita filtros `category` (`classica`|`promocional`) e `q` (busca
 *   textual, ILIKE em name/description no backend).
 * - POST `/api/campaigns/from-template/{slug}` clona o template em uma
 *   `Campaign` per-tenant (TH-55239) — endpoint real produção, faz o
 *   clone com snapshot de `template_version`/`category`/`payload`.
 *
 * Fallback mock: enquanto o backend não entra (ou em dev sem login), o
 * endpoint pode responder 404 — o hook degrada para um mock fixo para
 * que o resto do fluxo de UI fique navegável. Qualquer outro erro
 * (401/403/5xx) é propagado para a UI tratar como falha real (auth
 * quebrada não pode virar mock silencioso).
 */


import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

export interface CampaignTemplateRead {
  id: string;
  /** Slug único do template — chave da rota `POST /campaigns/from-template/{slug}`. */
  slug: string;
  name: string;
  /** Categoria de catálogo. Backend valida `^(classica|promocional)$`. */
  category: string;
  /** Family visual: `clean` | `cartazista` (opcional, ainda não em todos os mocks). */
  family?: string | null;
  thumb_url?: string | null;
  description?: string | null;
}

export interface CampaignTemplateListResponse {
  data: CampaignTemplateRead[];
}

export interface CampaignTemplateListFilters {
  category?: string;
  search?: string;
}

/**
 * Resultado do clone — campos mínimos consumidos pela UI (id para navegar,
 * name para feedback). Espelha um subconjunto de `CampaignRead` do backend.
 */
export interface CampaignCloneResult {
  id: string;
  name: string;
}

export interface CloneCampaignTemplateInput {
  /** Slug do template fonte (rota `POST /campaigns/from-template/{slug}`). */
  slug: string;
  /** Nome customizado da campanha. Se omitido, herda do template. */
  name?: string;
  /** Slug interno da campanha (opcional, gerado pelo backend a partir do name). */
  campaignSlug?: string;
}

/**
 * Mocks que cobrem dev sem backend. Slugs únicos casam com a rota real
 * `/api/campaigns/from-template/{slug}`. Categorias usam o vocabulário do
 * backend (`classica` | `promocional`). Family é exemplificada nas duas
 * variantes para que o card mostre o badge correto offline.
 */
const MOCK_TEMPLATES: CampaignTemplateRead[] = [
  {
    id: "mock-tpl-hortifruti",
    slug: "hortifruti-da-semana",
    name: "Hortifrúti da Semana",
    category: "classica",
    family: "clean",
    thumb_url: null,
    description: "Frutas, verduras e legumes do dia.",
  },
  {
    id: "mock-tpl-acougue",
    slug: "quarta-da-carne",
    name: "Quarta da Carne",
    category: "classica",
    family: "cartazista",
    thumb_url: null,
    description: "Cortes selecionados em destaque.",
  },
  {
    id: "mock-tpl-padaria",
    slug: "sexta-da-padaria",
    name: "Sexta da Padaria",
    category: "classica",
    family: "clean",
    thumb_url: null,
    description: "Pães, bolos e doces frescos.",
  },
  {
    id: "mock-tpl-mercearia",
    slug: "mercearia-em-oferta",
    name: "Mercearia em Oferta",
    category: "promocional",
    family: "cartazista",
    thumb_url: null,
    description: "Itens secos com preço de promo.",
  },
  {
    id: "mock-tpl-bebidas",
    slug: "verao-na-bebida",
    name: "Verão na Bebida",
    category: "promocional",
    family: "clean",
    thumb_url: null,
    description: "Refrigerantes, sucos e cervejas geladas.",
  },
];

function buildQuery(filters: CampaignTemplateListFilters): string {
  const sp = new URLSearchParams();
  if (filters.category) sp.set("category", filters.category);
  if (filters.search) sp.set("search", filters.search);
  return sp.toString();
}

async function fetchCampaignTemplates(
  filters: CampaignTemplateListFilters,
): Promise<CampaignTemplateListResponse> {
  const qs = buildQuery(filters);
  const url = qs ? `/api/campaign-templates?${qs}` : `/api/campaign-templates`;
  const res = await fetch(url, { credentials: "include" });

  // TODO TH-55140 backend — remover este branch quando o endpoint existir.
  // Branch específico em 404 só: 401/403/5xx continuam propagando como erro
  // para a UI tratar; auth quebrada não pode virar mock silencioso.
  if (res.status === 404) {
    return { data: MOCK_TEMPLATES };
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CampaignTemplateListResponse;
}

export function useCampaignTemplates(
  filters: CampaignTemplateListFilters = {},
): UseQueryResult<CampaignTemplateListResponse, Error> {
  return useQuery({
    queryKey: ["campaign-templates", filters],
    queryFn: () => fetchCampaignTemplates(filters),
    staleTime: 60_000,
  });
}

async function cloneCampaignTemplate(
  input: CloneCampaignTemplateInput,
): Promise<CampaignCloneResult> {
  const body: Record<string, string> = {};
  if (input.name) body.name = input.name;
  if (input.campaignSlug) body.slug = input.campaignSlug;

  const res = await fetch(
    `/api/campaigns/from-template/${encodeURIComponent(input.slug)}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
  );

  // Fallback dev — 404 significa que o backend ainda não está plugado neste
  // ambiente (ou está mockado). Latência simulada (1.5s) deixa o loading
  // state da UI exercitado em dev sem backend. Outros erros (401/403/5xx)
  // continuam propagando como falha real.
  if (res.status === 404) {
    await new Promise((r) => setTimeout(r, 1500));
    const generated =
      typeof globalThis.crypto?.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : `mock-${Date.now()}`;
    const template = MOCK_TEMPLATES.find((t) => t.slug === input.slug);
    return {
      id: generated,
      name:
        input.name ??
        (template ? `Cópia de ${template.name}` : "Cópia de template"),
    };
  }

  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(errBody.detail ?? `HTTP ${res.status}`);
  }
  // Backend retorna `CampaignRead` completo — extrair só id+name para UI.
  const payload = (await res.json()) as { id: string; name: string };
  return { id: payload.id, name: payload.name };
}

export function useCloneCampaignTemplate(): UseMutationResult<
  CampaignCloneResult,
  Error,
  CloneCampaignTemplateInput
> {
  return useMutation({
    mutationFn: cloneCampaignTemplate,
  });
}

// Reexportado pra testes que queiram inspecionar o conjunto base.
export const __MOCK_CAMPAIGN_TEMPLATES = MOCK_TEMPLATES;
